// src/components/orders/ApplyDiscountModal.tsx
import React, { useMemo, useState } from "react";
import Card from "../ui/Card";
import Field from "../ui/Field";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Button from "../ui/Button";
import { OrderItem } from "../../services/types/orders.types";

interface ApplyDiscountModalProps {
    isOpen: boolean;
    order: OrderItem;
    onClose: () => void;
    onSubmit: (discountType: "amount" | "percent", discountValue: number) => Promise<void> | void;
}

const ApplyDiscountModal: React.FC<ApplyDiscountModalProps> = ({
                                                                     isOpen,
                                                                     order,
                                                                     onClose,
                                                                     onSubmit,
                                                                 }) => {
    const [discountType, setDiscountType] = useState<"amount" | "percent">("amount");
    const [discountValue, setDiscountValue] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const ticketsCount = useMemo(() => {
        return (order.tours || []).reduce((sum, tour) => sum + (tour.items?.length || 0), 0);
    }, [order.tours]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        const value = Number(discountValue.replace(",", "."));
        if (!value || value <= 0) {
            setError("Введите сумму или процент скидки больше нуля");
            return;
        }
        if (discountType === "percent" && value > 100) {
            setError("Процент скидки не может быть больше 100");
            return;
        }

        try {
            setIsSaving(true);
            await onSubmit(discountType, value);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Не удалось применить скидку",
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop">
            <div className="modal-dialog">
                <Card className="notopacity">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">Применить скидку</h3>
                            <p className="card-text">
                                Заказ {order.order_number}. Билетов в заказе: {ticketsCount}.
                                Скидка будет распределена между всеми билетами.
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        {error && <div className="form-global-error">{error}</div>}

                        <Field label="Тип скидки" htmlFor="discount-type">
                            <Select
                                id="discount-type"
                                value={discountType}
                                onChange={(e) =>
                                    setDiscountType(e.target.value as "amount" | "percent")
                                }
                                options={[
                                    { value: "amount", label: "Точная сумма (€), поровну на все билеты" },
                                    { value: "percent", label: "Процент от цены каждого билета" },
                                ]}
                            />
                        </Field>

                        <Field
                            label={discountType === "percent" ? "Процент скидки" : "Сумма скидки (€)"}
                            htmlFor="discount-value"
                        >
                            <Input
                                id="discount-value"
                                type="number"
                                step="0.01"
                                min="0"
                                max={discountType === "percent" ? 100 : undefined}
                                value={discountValue}
                                onChange={(e) => setDiscountValue(e.target.value)}
                                placeholder={discountType === "percent" ? "например, 10" : "например, 10"}
                            />
                        </Field>

                        <div className="editor-actions">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                Отмена
                            </Button>
                            <Button type="submit" variant="primary" disabled={isSaving}>
                                {isSaving ? "Применяем..." : "Применить скидку"}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};

export default ApplyDiscountModal;
